// Story 6.3 (ADO-879, AC4) — non-authoritative session context for the SPA;
// realigned by Story 6.32 (ADO-1315) to the BWG source-of-truth model.
//
// Exposes identity (sub/name/email from the ID token — standard OIDC claims,
// exactly what the BWG integration guide's "ID Token" section documents) plus
// tenant/role/depot/accountType from the SERVER session read
// (`GET /membership/tenant-context`). The BWG tenant issues NO `shoplink/*`
// custom claims (no Login Action; access decisions key on `sub` against the
// SL2 user table), so the former claim-derived role/tenant/depot mapping is
// gone. This is a DISPLAY / ROUTING affordance ONLY: per NFR18
// (AUTH_FLOW_SPEC §3) `apps/api` re-validates every request, so the SPA must
// NEVER gate an authorisation decision on this state. It is held in Zustand
// (ADR-004); cleared on logout and on a terminal refresh. Story 8.1 (ADO-2010,
// A1.5): the active selection (entry, Trading Account, depot) stays in Zustand
// only. The one thing the device keeps is the remembered-choice HINT in
// `localStorage` (`ordering/rememberedHint.ts`), which the entry flow re-validates
// against the offered set before using it, and which the server never trusts.
//
// Story 8.1 (ADO-2010), Task 5.4: the ordering slice. `GET /api/v1/ordering/choices`
// says where the caller may order. For a store user (`TRADING_ACCOUNTS`) the offered
// depots ARE the entries' depots, so `depotIds` / `depotNames` are derived from the
// entries HERE, at the store, so both depot surfaces (DepotPicker, and the SiteHeader
// switcher via useHeaderChrome) read the same set, and `activeDepot` (what
// useActiveBasket opens) is always the active entry's depot. Staff (`DEPOT_IDS`) keep
// `users.depot_ids` from the server session.
//
// Enabler E8-G (ADO-3109), DA-10 (AC11): a staff user has no depot-keyed cart any more. It picks
// a store account at the depot (`chooseStaffAccount`, from `GET /api/v1/ordering/accounts`), which
// becomes its active Trading Account, and every cart call there names it, as a store user's
// does. A staff entry offers no accounts, so the pick is held while the active entry stays at
// that depot (Chill to Fruit & Veg keeps it) and dropped on a move to another depot, after which
// the browsing gate sends the staff user back to pick one (`requiresOrderingChoice`).

import { create } from 'zustand';
import { DEFAULT_BRAND, type WebBrandConfig } from '../theme/brand';
import type {
  OrderingAccount,
  OrderingChoices,
  OrderingEntry,
  OrderingStaffAccount,
} from '../api/ordering-choices.client';
import { primaryEntryAtDepot, resolveChoice } from '../ordering/orderingChoices';

/** Minimal identity surfaced for display — never used for trust decisions. */
export interface SessionIdentity {
  readonly sub: string;
  readonly name?: string;
  readonly email?: string;
}

/** The server-owned session fields (`GET /membership/tenant-context`). */
export interface ServerSession {
  readonly tenantId: string | null;
  readonly role: string | null;
  readonly depotIds: readonly string[];
  readonly accountType: string | null;
  /** Story 7.16 (ADO-1373) — the caller's store code; `null` when unprovisioned. */
  readonly customerAccountCode: string | null;
  /** Resolved per-tenant brand (Story 6.35 / ADO-1693); default brand when absent. */
  readonly brandConfig?: WebBrandConfig;
}

/** The non-authoritative session context exposed to the SPA (AC4). */
export interface SessionContext {
  /** `sub` (+ name/email) from the ID-token claims (standard OIDC). */
  identity: SessionIdentity | null;
  /** Registry-resolved role from the server read — display/routing only. */
  role: string | null;
  /** Registry-resolved tenant from the server read — display only. */
  tenantId: string | null;
  /** Registry-resolved depots this principal may act for. */
  depotIds: readonly string[];
  /** Client-only UI selection over {@link depotIds} (defaults to the first). */
  activeDepot: string | null;
  /**
   * Story 7.22 (ADO-1703) — REAL depot names keyed by depot id, from
   * `GET /api/v1/session/depots` (the Story 14.9 VC master feed). Replaces the
   * hashed "VC ..." placeholder `depotDisplay.ts` used to invent. A depot with no
   * entry (endpoint not yet resolved, or unresolved server-side) has its id/code
   * shown as the neutral fallback — NEVER a fabricated name. Non-authoritative
   * display data (NFR18), Zustand only.
   */
  depotNames: Readonly<Record<string, string>>;
  /** From the TenantContext-backed server read; null until that read resolves. */
  accountType: string | null;
  /**
   * Story 7.16 (ADO-1373) part C — the caller's STORE code: the BWG /
   * central-billing customer code, `X`-stripped, that the store EPOS
   * sales/deliveries feeds join on. The Stock Card passes it as `storeCode`.
   *
   * `null` when unprovisioned (the normal case today — ADO #1700) OR before the
   * server read resolves, and the Stock Card renders nothing in both cases (AC5).
   * Like every other field here it is DISPLAY affordance only, never a trust
   * decision: the API re-scopes the read by tenant regardless.
   */
  customerAccountCode: string | null;
  /**
   * Resolved per-tenant white-label brand (Story 6.35 / ADO-1693). Defaults to
   * the ShopLink brand until the server session read resolves; cleared back to
   * the default on logout. `BrandThemeApplier` applies it to the document root.
   */
  brandConfig: WebBrandConfig;
  /** Story 8.1 (ADO-2010): the ordering-choices read state. `error` is a failed read,
   *  distinct from a `ready` read with no entries (unlinked, A2.2). */
  orderingStatus: OrderingStatus;
  /** The last successful ordering-choices read; `null` until one lands. */
  orderingChoices: OrderingChoices | null;
  /** The chosen ordering entry (`OrderingEntry.key`), `null` until one is chosen. */
  activeEntryKey: string | null;
  /** The chosen Trading Account at the active entry (A4.4); `null` when a multi-account depot
   *  was reached by depot alone, and for staff until they pick a store account (Enabler E8-G,
   *  ADO-3109, DA-10: then the picked account). */
  activeTradingAccount: OrderingAccount | null;
}

export type OrderingStatus = 'idle' | 'loading' | 'ready' | 'error';

export interface SessionState extends SessionContext {
  /** Populate identity (sub/name/email) from the ID-token claims. */
  setFromClaims: (claims: Readonly<Record<string, unknown>> | null | undefined) => void;
  /** Populate tenant/role/depots/accountType from the server session read (6.32). */
  setFromServerSession: (session: ServerSession | null) => void;
  /** Set `accountType` alone (kept for targeted updates/tests). */
  setAccountType: (accountType: string | null) => void;
  /** Change the active depot (client-only UI selection; non-authoritative). */
  setActiveDepot: (depotId: string | null) => void;
  /** Story 7.22 (ADO-1703) — set the resolved `{ id → name }` depot-name map. */
  setDepotNames: (depotNames: Readonly<Record<string, string>>) => void;
  /** Story 8.1 (ADO-2010): the ordering-choices read is in flight (keeps prior data). */
  setOrderingLoading: () => void;
  /** Story 8.1 (ADO-2010): the ordering-choices read failed (keeps prior data). */
  setOrderingFailed: () => void;
  /** Story 8.1 (ADO-2010): apply a successful ordering-choices read. A still-offered
   *  active choice survives a refresh; one no longer offered is dropped. */
  setOrderingChoices: (choices: OrderingChoices) => void;
  /**
   * Story 8.1 (ADO-2010): make `entryKey` (+ `customerAccountCode`) the active choice and
   * its depot the active depot. Refused (returns false, state untouched) when it is not
   * in the offered set, or when a multi-account entry is named without an account.
   */
  chooseEntry: (entryKey: string, customerAccountCode: string | null) => boolean;
  /**
   * Enabler E8-G (ADO-3109), DA-10 (AC11): a staff user makes `entryKey` the active entry with
   * the store `account` it picked as the active Trading Account, and its depot the active depot.
   * Refused (returns false, state untouched) for a store user, or when the entry is not offered.
   * The server checks the account on every cart call.
   */
  chooseStaffAccount: (entryKey: string, account: OrderingStaffAccount) => boolean;
  /** Clear all session context (logout / terminal refresh). */
  clear: () => void;
}

const EMPTY: SessionContext = {
  identity: null,
  role: null,
  tenantId: null,
  depotIds: [],
  activeDepot: null,
  depotNames: {},
  accountType: null,
  customerAccountCode: null,
  brandConfig: DEFAULT_BRAND,
  orderingStatus: 'idle',
  orderingChoices: null,
  activeEntryKey: null,
  activeTradingAccount: null,
};

function asString(value: unknown): string | null {
  return typeof value === 'string' && value !== '' ? value : null;
}

/**
 * Story 7.16 (ADO-1373) — a store code that is blank once trimmed is ABSENT, not
 * a store. Returns the trimmed value, so no request ever carries padding.
 *
 * The guard is `typeof value !== 'string'`, NOT `value === null`, even though the
 * declared type says `string | null`. `ServerSession` is a WIRE shape: it is built
 * by `fetchTenantSession` from `res.json()`, and an older API (or a test double
 * that predates this field) simply has no such key, which arrives as `undefined`
 * and made a `=== null` guard throw on `.trim()`. `brandConfig` on the same object
 * is defensive for exactly the same reason.
 */
function presentCode(value: string | null | undefined): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed === '' ? null : trimmed;
}

/**
 * Pure mapper from ID-token claims to the identity slice of the session
 * context. Exported for direct unit testing. Story 6.32: identity ONLY —
 * role/tenant/depots come from {@link SessionState.setFromServerSession}.
 */
export function mapClaimsToContext(
  claims: Readonly<Record<string, unknown>> | null | undefined,
): Pick<SessionContext, 'identity'> {
  if (claims == null) {
    return { identity: null };
  }
  const sub = asString(claims['sub']);
  const name = asString(claims['name']);
  const email = asString(claims['email']);

  return {
    identity:
      sub !== null
        ? { sub, ...(name !== null ? { name } : {}), ...(email !== null ? { email } : {}) }
        : null,
  };
}

/** Pure mapper from the server session read to the store slice. Exported for tests. */
export function mapServerSessionToContext(
  session: ServerSession | null,
): Pick<
  SessionContext,
  | 'role'
  | 'tenantId'
  | 'depotIds'
  | 'activeDepot'
  | 'accountType'
  | 'customerAccountCode'
  | 'brandConfig'
> {
  if (session === null) {
    return {
      role: null,
      tenantId: null,
      depotIds: [],
      activeDepot: null,
      accountType: null,
      customerAccountCode: null,
      brandConfig: DEFAULT_BRAND,
    };
  }
  // Story 7-FU (ADO-1750), AC3 — filter non-strings/empties AND de-duplicate here,
  // at the single source, preserving first-seen order. `depotIds` feeds BOTH depot
  // pickers (the `DepotPicker` control and the `SiteHeader` dropdown via
  // `useHeaderChrome`), so deduping once at the store fixes duplicate React keys on
  // both, and makes cardinality coherent everywhere: a claim of `["A","A"]` is ONE
  // real depot, so the `.length === 1` gate below correctly auto-selects it rather
  // than treating two identical ids as a multi-depot choice.
  const depotIds = [
    ...new Set(session.depotIds.filter((d): d is string => typeof d === 'string' && d !== '')),
  ];
  return {
    role: session.role,
    tenantId: session.tenantId,
    depotIds,
    // Story 8.1 (ADO-1092), AC1 / D-3 — the depot-selection GATE. Do NOT silently
    // auto-default a multi-depot user to `depotIds[0]`: a basket is scoped to a
    // depot, so an unchosen depot must block Add until the user picks one
    // explicitly (SL1.0 force-routes to the depot picker and never auto-picks).
    // A user with exactly ONE permitted depot (after dedupe) has nothing to choose,
    // so keep the auto-select for that case (the design's "1 → read-only" degrade).
    activeDepot: depotIds.length === 1 ? (depotIds[0] ?? null) : null,
    accountType: session.accountType,
    // Story 7.16 (ADO-1373) — trimmed, and BLANK-IS-ABSENT. A whitespace-only code
    // would otherwise be sent verbatim as `storeCode` and 400 at the API (the
    // controller trims and rejects empty), turning "this user has no store" into a
    // failed request. Absent is the render AC5 asks for: no Stock Card at all.
    customerAccountCode: presentCode(session.customerAccountCode),
    brandConfig: session.brandConfig ?? DEFAULT_BRAND,
  };
}

/**
 * Story 7.22 (ADO-1703) — the depot label every SPA surface renders: the real
 * name from {@link SessionContext.depotNames} when resolved, else the id
 * itself — never a fabricated placeholder. Shared by `useHeaderChrome` and
 * `DepotPicker` so the fallback is implemented once, not independently in
 * each (Review Findings, code review 2026-09-01).
 */
export function depotNameFor(depotNames: Readonly<Record<string, string>>, id: string): string {
  return depotNames[id] ?? id;
}

/** Story 8.1 (ADO-2010): each entry depot's display name, the DEPOT entry's label winning
 *  over a range that shares the depot (Chill over Fruit & Veg). */
function entryDepotNames(entries: readonly OrderingEntry[]): Record<string, string> {
  const names: Record<string, string> = {};
  for (const entry of entries) {
    if (entry.kind === 'DEPOT' || names[entry.depotId] === undefined) {
      names[entry.depotId] = entry.label;
    }
  }
  return names;
}

/**
 * Story 8.1 (ADO-2010): bring the depot slice and the active choice in line with the
 * ordering choices. The single place both are derived, so the order in which the
 * session read, the depot-name read and the choices read land does not matter.
 *
 *   - no choices yet: the server session's depots stand, and there is no active entry;
 *   - store user: `depotIds` = the entries' depots (deduped, entry order), the active
 *     depot is the active entry's depot, and a choice no longer offered is dropped;
 *   - staff: `depotIds` stay as the server session set them; a picked store account is kept
 *     while its entry is still offered (Enabler E8-G, ADO-3109, DA-10), and otherwise the
 *     active entry follows the active depot with no account (so the single-depot auto-set
 *     still selects one), which the browsing gate answers by asking for a store account.
 */
function reconcileOrdering(state: SessionContext): SessionContext {
  const choices = state.orderingChoices;
  if (choices === null) return { ...state, activeEntryKey: null, activeTradingAccount: null };
  const storeUser = choices.scope === 'TRADING_ACCOUNTS';
  const depotIds = storeUser
    ? [...new Set(choices.entries.map((entry) => entry.depotId))]
    : state.depotIds;
  const depotNames = storeUser
    ? { ...state.depotNames, ...entryDepotNames(choices.entries) }
    : state.depotNames;

  const current = choices.entries.find((entry) => entry.key === state.activeEntryKey);
  // Enabler E8-G (ADO-3109), DA-10: a staff entry lists no accounts, so a picked store account
  // stays while its entry is still offered.
  if (!storeUser && current !== undefined && state.activeTradingAccount !== null) {
    return { ...state, depotIds, depotNames, activeDepot: current.depotId };
  }
  const currentAccount =
    current === undefined || state.activeTradingAccount === null
      ? null
      : (current.accounts.find(
          (account) =>
            account.customerAccountCode === state.activeTradingAccount?.customerAccountCode,
        ) ?? undefined);
  if (current !== undefined && currentAccount !== undefined) {
    return {
      ...state,
      depotIds,
      depotNames,
      activeDepot: current.depotId,
      activeTradingAccount: currentAccount,
    };
  }
  if (!storeUser && state.activeDepot !== null) {
    const atDepot = primaryEntryAtDepot(choices.entries, state.activeDepot) ?? undefined;
    return {
      ...state,
      depotIds,
      depotNames,
      activeEntryKey: atDepot?.key ?? null,
      activeTradingAccount: null,
    };
  }
  return {
    ...state,
    depotIds,
    depotNames,
    activeDepot: storeUser ? null : state.activeDepot,
    activeEntryKey: null,
    activeTradingAccount: null,
  };
}

export const useSessionStore = create<SessionState>((set, get) => ({
  ...EMPTY,
  setFromClaims: (claims) => set(mapClaimsToContext(claims)),
  setFromServerSession: (session) =>
    set((state) => reconcileOrdering({ ...state, ...mapServerSessionToContext(session) })),
  setAccountType: (accountType) => set({ accountType }),
  // Story 8.1 (ADO-2010): guarded at the store. For a store user a depot outside the
  // offered set is ignored, and a move to another depot moves the active entry with it
  // (its sole account, or none at a multi-account depot). Before the choices load the depot is
  // set as before. A staff user moving to another depot loses the store account picked at the
  // old one (Enabler E8-G, ADO-3109, DA-10); staying at the depot keeps it.
  setActiveDepot: (depotId) =>
    set((state) => {
      const choices = state.orderingChoices;
      if (choices === null) return { activeDepot: depotId };
      if (depotId === null) {
        return { activeDepot: null, activeEntryKey: null, activeTradingAccount: null };
      }
      const current = choices.entries.find((entry) => entry.key === state.activeEntryKey);
      if (current?.depotId === depotId) return { activeDepot: depotId };
      const atDepot = primaryEntryAtDepot(choices.entries, depotId) ?? undefined;
      if (atDepot === undefined) {
        return choices.scope === 'TRADING_ACCOUNTS'
          ? state
          : { activeDepot: depotId, activeEntryKey: null, activeTradingAccount: null };
      }
      return {
        activeDepot: depotId,
        activeEntryKey: atDepot.key,
        activeTradingAccount: atDepot.accounts.length === 1 ? (atDepot.accounts[0] ?? null) : null,
      };
    }),
  setDepotNames: (depotNames) => set((state) => reconcileOrdering({ ...state, depotNames })),
  setOrderingLoading: () => set({ orderingStatus: 'loading' }),
  setOrderingFailed: () => set({ orderingStatus: 'error' }),
  setOrderingChoices: (choices) =>
    set((state) =>
      reconcileOrdering({ ...state, orderingChoices: choices, orderingStatus: 'ready' }),
    ),
  chooseEntry: (entryKey, customerAccountCode) => {
    const state = get();
    const choices = state.orderingChoices;
    if (choices === null) return false;
    const resolved = resolveChoice(choices, entryKey, customerAccountCode);
    if (resolved === null) return false;
    // Enabler E8-G (ADO-3109), DA-10: a staff entry offers no account; the store account picked
    // at the depot stays with a move between its entries (Chill to Fruit & Veg) and goes with a
    // move to another depot.
    const keptStaffAccount =
      choices.scope === 'DEPOT_IDS' && state.activeDepot === resolved.entry.depotId
        ? state.activeTradingAccount
        : null;
    set({
      activeEntryKey: resolved.entry.key,
      activeTradingAccount: resolved.account ?? keptStaffAccount,
      activeDepot: resolved.entry.depotId,
    });
    return true;
  },
  chooseStaffAccount: (entryKey, account) => {
    const choices = get().orderingChoices;
    if (choices === null || choices.scope !== 'DEPOT_IDS') return false;
    const entry = choices.entries.find((candidate) => candidate.key === entryKey);
    if (entry === undefined) return false;
    set({
      activeEntryKey: entry.key,
      activeDepot: entry.depotId,
      // The store's own account type, so the staff user sees what the store's users see.
      activeTradingAccount: {
        customerAccountCode: account.customerAccountCode,
        accountName: account.accountName,
        tradingAccountType: account.tradingAccountType,
      },
    });
    return true;
  },
  clear: () => set({ ...EMPTY }),
}));
